import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Header1 from "../Header/Header1";
import Footer2 from "../Footer/Footer2";

const API_URL = process.env.REACT_APP_API_URL;

export default function NewsletterSubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // Search
  const [search, setSearch] = useState("");

  const fetchSubscriptions = async () => {
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/newsletter-subscriptions/`);
      const data = await response.json();

      const results = Array.isArray(data) ? data : data.results;
      const cleaned = results || [];

      setSubscriptions(cleaned);
      setFilteredSubscriptions(cleaned);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    const filtered = subscriptions.filter((item) =>
      item.email.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredSubscriptions(filtered);
    setPage(0);
  }, [search, subscriptions]);

  const paginatedData = filteredSubscriptions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const downloadCSV = () => {
    const csvHeader = "Email,Created At\n";
    const csvRows = filteredSubscriptions
      .map((item) => `${item.email},${item.created_at}`)
      .join("\n");

    const csvContent = csvHeader + csvRows;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "newsletter_subscriptions.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div style={{ backgroundColor: "#000000ff", minHeight: "100vh" }}>
      <Header1 />

      <Box
        sx={{
          p: 4,
          maxWidth: "1100px",
          margin: "auto",
          color: "#e6edf3",
        }}
      >
        {/* HEADER SECTION */}
        <Paper
          elevation={5}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: "16px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography variant="h4" fontWeight="bold" color="#58a6ff" mb={1}>
            Newsletter Subscriptions
          </Typography>
          <Typography variant="body1" color="#8b949e">
            View, filter, and download your newsletter subscriber list.
          </Typography>
        </Paper>

        {/* SEARCH + DOWNLOAD */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          mb={3}
          alignItems="center"
        >
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Search subscribers by email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              background: "rgba(255,255,255,0.06)",
              borderRadius: "12px",
              input: { color: "#e6edf3" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": {
                  borderColor: "rgba(255,255,255,0.15)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(88,166,255,0.6)",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#8b949e" }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={downloadCSV}
            disabled={filteredSubscriptions.length === 0}
            sx={{
              height: "55px",
              px: 3,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #238636, #15692e)",
              "&:hover": {
                background: "linear-gradient(135deg, #2ea043, #1f7a39)",
              },
            }}
          >
            Download CSV
          </Button>
        </Stack>

        {/* TABLE */}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Paper
            elevation={4}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              backgroundColor: "#161b22",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <TableContainer sx={{ maxHeight: "500px" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#21262d" }}>
                    <TableCell sx={{ color: "#c9d1d9", fontWeight: "bold" }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ color: "#c9d1d9", fontWeight: "bold" }}>
                      Created At
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((sub) => (
                    <TableRow
                      key={sub.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(56,139,253,0.1)",
                        },
                        transition: "0.2s",
                      }}
                    >
                      <TableCell sx={{ color: "#e6edf3" }}>
                        {sub.email}
                      </TableCell>
                      <TableCell sx={{ color: "#8b949e" }}>
                        {new Date(sub.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}

                  {paginatedData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                        <Typography color="#8b949e">
                          No results found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

            {/* PAGINATION */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              p={2}
            >
              <IconButton
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                sx={{
                  borderRadius: "10px",
                  color: "#c9d1d9",
                  border: "1px solid rgba(255,255,255,0.1)",
                  "&:hover": { backgroundColor: "rgba(56,139,253,0.1)" },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Typography fontWeight="bold" color="#c9d1d9">
                Page {page + 1} of{" "}
                {Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1}
              </Typography>

              <IconButton
                disabled={(page + 1) * rowsPerPage >= filteredSubscriptions.length}
                onClick={() => setPage(page + 1)}
                sx={{
                  borderRadius: "10px",
                  color: "#c9d1d9",
                  border: "1px solid rgba(255,255,255,0.1)",
                  "&:hover": { backgroundColor: "rgba(56,139,253,0.1)" },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Paper>
        )}
      </Box>

      <Footer2 />
    </div>
  );
}
